import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Button, Flex, HStack, Tab, TabList, Tabs, Text, VStack } from "@chakra-ui/react";
import { LocalDate, nativeJs } from "@js-joda/core";
import React from "react";
import RangeDatePicker from "../../shared/components/DatePicker/RangeDatePicker";
import { PatientTaskCustomizeDayTimeParamsPattern } from "./PatientTaskCustomizeDayTimeParamsPattern";
import { PatientTaskCustomizeDayTimeParamsTypes as Types } from "./PatientTaskCustomizeDayTimeParams.types";
import { patientTaskCustomizeDayTimeParamsUtils as utils } from "./PatientTaskCustomizeDayTimeParams.utils";
import { PatientTaskCustomizeDayTimeParamsSpecificDates } from "./PatientTaskCustomizeDayTimeParamsSpecificDates";

export default function PatientTaskCustomizeDayTimeParams(props: {
  dateRange: Types.DateRangeVanilla;
  dayTimeScheduleOverrideParams?: Types.DayTimeParams | null;
  disableDates: boolean;
  onChange: (params: {
    params: {
      dateRange: Types.DateRange;
      dayTimeScheduleOverrideParams: Types.DayTimeParams | null;
    };
  }) => void;
}) {
  const [showCustomizedDayTimes, setShowCustomizedDayTimes] = React.useState(
    props.dayTimeScheduleOverrideParams !== null &&
      props.dayTimeScheduleOverrideParams !== undefined
  );
  const [dateRange, setDateRange] = React.useState<Types.DateRange>([
    LocalDate.from(nativeJs(new Date(props.dateRange[0]))),
    LocalDate.from(nativeJs(new Date(props.dateRange[1]))),
  ]);
  const [dayTimeScheduleOverrideParams, setDayTimeScheduleOverrideParams] =
    React.useState<Types.DayTimeParams>(
      utils.parseDayTimeParamsIfNeeded(props.dayTimeScheduleOverrideParams) ??
        utils.generateDefaultDayTimeParamsByDayOfWeek(dateRange)
    );

  React.useEffect(() => {
    if (dateRange[1] === null) {
      return;
    }
    props.onChange({
      params: {
        dateRange,
        dayTimeScheduleOverrideParams: showCustomizedDayTimes
          ? dayTimeScheduleOverrideParams
          : null,
      },
    });
  }, [dateRange, dayTimeScheduleOverrideParams, props, showCustomizedDayTimes]);

  const handleToggleMethod = () => {
    if (dayTimeScheduleOverrideParams.type === "SpecificDates") {
      setDayTimeScheduleOverrideParams(utils.generateDefaultDayTimeParamsByDayOfWeek(dateRange));
    } else {
      setDayTimeScheduleOverrideParams(utils.generateDefaultDayTimeParamsByDate(dateRange));
    }
  };

  const handleChangeDateRange = (dates: Types.DateRange) => {
    setDateRange(dates);
    setDayTimeScheduleOverrideParams(
      dayTimeScheduleOverrideParams.type === "SpecificDates"
        ? utils.generateDefaultDayTimeParamsByDate(dates)
        : utils.generateDefaultDayTimeParamsByDayOfWeek(dates)
    );
  };

  function handleChangeMethod(method: "SpecificDates" | "Pattern") {
    if (dayTimeScheduleOverrideParams.type === method) {
      return;
    }
    handleToggleMethod();
  }

  return (
    <Flex flexDirection="column" gap={4} py={4}>
      <VStack alignItems="flex-start" gap={2}>
        <Text fontWeight="bold">Pick start & due date</Text>
        <HStack alignItems="center">
          <RangeDatePicker
            disabled={props.disableDates}
            endDate={dateRange[1]}
            hideDateRanges={true}
            isClearable={false}
            startDate={dateRange[0]}
            onChange={(dates) => {
              if (dates === null) {
                return;
              }
              handleChangeDateRange(dates);
            }}
          />
          <Button
            backgroundColor="blue.100"
            color="blue.500"
            fontSize="sm"
            rightIcon={
              showCustomizedDayTimes ? (
                <ChevronUpIcon boxSize={6} />
              ) : (
                <ChevronDownIcon boxSize={6} />
              )
            }
            rounded="full"
            size="sm"
            onClick={() => setShowCustomizedDayTimes(!showCustomizedDayTimes)}
          >
            Customize days & hours
          </Button>
        </HStack>
      </VStack>

      {showCustomizedDayTimes ? (
        <Flex direction="column" gap={4}>
          <Tabs
            isFitted
            isManual
            index={paramsTypeToTabIndex[dayTimeScheduleOverrideParams.type]}
            onChange={(index) => {
              if (index === 0 || index === 1) {
                handleChangeMethod(tabIndexToParamsType[index]);
              }
            }}
          >
            <TabList>
              <Tab>Pattern</Tab>
              <Tab>Specific Dates</Tab>
            </TabList>
          </Tabs>
          {dayTimeScheduleOverrideParams.type === "SpecificDates" ? (
            <PatientTaskCustomizeDayTimeParamsSpecificDates
              dateRange={dateRange}
              dayTimeScheduleOverrideParams={dayTimeScheduleOverrideParams}
              onChange={setDayTimeScheduleOverrideParams}
            />
          ) : (
            <PatientTaskCustomizeDayTimeParamsPattern
              dateRange={dateRange}
              dayTimeScheduleOverrideParams={dayTimeScheduleOverrideParams}
              onChange={setDayTimeScheduleOverrideParams}
            />
          )}
        </Flex>
      ) : null}
    </Flex>
  );
}

const paramsTypeToTabIndex = {
  Pattern: 0,
  SpecificDates: 1,
};

const tabIndexToParamsType = {
  0: "Pattern",
  1: "SpecificDates",
} as const;
